import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ProcessingSortMenu.css";

const SORT_OPTIONS = [
  { value: "due", label: "Due", detail: "Closest to its prep deadline" },
  { value: "newest", label: "Newest first", detail: "Most recently received" },
  { value: "oldest", label: "Oldest first", detail: "Waiting in the kitchen longest" },
  { value: "quickest", label: "Quickest prep time", detail: "Shortest estimated preparation" },
];

const ProcessingSortMenu = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const activeIndexRef = useRef(0);
  const selected = SORT_OPTIONS.find((option) => option.value === value) || SORT_OPTIONS[0];

  const updatePosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.min(292, window.innerWidth - 16);
    const height = Math.min(300, window.innerHeight - 16);
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    const top = rect.bottom + 7 + height <= window.innerHeight
      ? rect.bottom + 7
      : Math.max(8, rect.top - height - 7);
    setPosition({ left, top, width, maxHeight: height });
  };

  const openMenu = (index = SORT_OPTIONS.findIndex((option) => option.value === value)) => {
    activeIndexRef.current = Math.max(0, index);
    updatePosition();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const handleOutside = (event) => {
      if (!menuRef.current?.contains(event.target) && !triggerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleLayout = () => updatePosition();
    document.addEventListener("pointerdown", handleOutside);
    window.addEventListener("resize", handleLayout);
    window.addEventListener("scroll", handleLayout, true);
    return () => {
      document.removeEventListener("pointerdown", handleOutside);
      window.removeEventListener("resize", handleLayout);
      window.removeEventListener("scroll", handleLayout, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) menuRef.current?.querySelector(`[data-sort-index="${activeIndexRef.current}"]`)?.focus();
  }, [open]);

  const close = (restoreFocus = false) => {
    setOpen(false);
    // Skip if focus already moved elsewhere (e.g. the user clicked another control).
    if (restoreFocus) {
      requestAnimationFrame(() => {
        const active = document.activeElement;
        if (!active || active === document.body || menuRef.current?.contains(active)) triggerRef.current?.focus();
      });
    }
  };

  const handleKeyDown = (event) => {
    const currentIndex = activeIndexRef.current;
    if (event.key === "Escape") {
      event.preventDefault();
      close(true);
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      activeIndexRef.current = (currentIndex + (event.key === "ArrowDown" ? 1 : -1) + SORT_OPTIONS.length) % SORT_OPTIONS.length;
      menuRef.current?.querySelector(`[data-sort-index="${activeIndexRef.current}"]`)?.focus();
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      activeIndexRef.current = event.key === "Home" ? 0 : SORT_OPTIONS.length - 1;
      menuRef.current?.querySelector(`[data-sort-index="${activeIndexRef.current}"]`)?.focus();
    } else if (event.key === "Tab") {
      close();
    }
  };

  const selectOption = (option) => {
    onChange(option.value);
    close(true);
  };

  const fullscreenContainer = typeof document !== "undefined" && document.fullscreenElement;
  const isDark = triggerRef.current?.closest(".dark") != null;

  return (
    <div className="processing-sort-anchor">
      <button
        ref={triggerRef}
        type="button"
        className={`processing-sort-trigger${open ? " is-open" : ""}`}
        aria-label={`Sort orders, currently ${selected.label}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={(event) => {
          if (!open && ["ArrowDown", "Enter", " "].includes(event.key)) {
            event.preventDefault();
            openMenu();
          }
        }}
      >
        <span className="processing-sort-current">{selected.label}</span>
        <svg className="processing-sort-chevron" viewBox="0 0 16 16" aria-hidden="true" fill="none">
          <path d="m4 6 4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && position && createPortal(
        <div
          ref={menuRef}
          className={`processing-sort-menu${isDark ? " is-dark" : ""}`}
          role="listbox"
          aria-label="Sort processing orders"
          style={{ left: position.left, top: position.top, width: position.width, maxHeight: position.maxHeight }}
          onKeyDown={handleKeyDown}
        >
          <div className="processing-sort-menu-heading">
            <span>Sort orders</span>
            <span className="processing-sort-menu-hint">Choose a view</span>
          </div>
          {SORT_OPTIONS.map((option, index) => {
            const active = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={active}
                tabIndex={-1}
                data-sort-index={index}
                className={`processing-sort-option${active ? " is-selected" : ""}`}
                onFocus={() => { activeIndexRef.current = index; }}
                onClick={() => selectOption(option)}
              >
                <span className="processing-sort-radio" aria-hidden="true">
                  {active && <span />}
                </span>
                <span className="processing-sort-option-copy">
                  <span className="processing-sort-option-label">{option.label}</span>
                  <span className="processing-sort-option-detail">{option.detail}</span>
                </span>
                {active && (
                  <svg className="processing-sort-check" viewBox="0 0 16 16" aria-hidden="true" fill="none">
                    <path d="m3 8 3.2 3.2L13 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>,
        fullscreenContainer || document.body,
      )}
    </div>
  );
};

export default ProcessingSortMenu;
